import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChartNoAxesColumn, ChevronRight, LogOut, Pencil, Settings2, ShieldCheck } from "lucide-react";
import { AccountUsage } from "./AccountUsage";
import { Modal } from "./Modal";
import { agentStyleChoices, drawingColorChoices, getPreferences, savePreferences, type AgentStyle } from "./preferences";
import { hasLocalAgents } from "./AgentIdentity";
import { notify, type User } from "./api";

export function AccountMenu({
  user,
  saveName,
  signOut,
}: {
  user: User;
  saveName?: (name: string) => Promise<void>;
  signOut?: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name);
  const [busy, setBusy] = useState<"save" | "sign-out" | null>(null);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => getPreferences(user.id));
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const showUsage = () => {
    if (!signOut) return;
    close();
    setUsage(true);
  };
  useEffect(() => {
    window.addEventListener("planroom:account", showUsage);
    return () => window.removeEventListener("planroom:account", showUsage);
  }, [signOut]);
  useEffect(() => {
    if (!open) return;
    root.current
      ?.querySelector<HTMLElement>(
        editing ? ".account-profile-form input" : ".account-popover button",
      )
      ?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target))
        setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open, editing]);
  return (
    <>
      <div
        className="account-menu"
        ref={root}
        onBlur={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            !event.currentTarget.contains(event.relatedTarget)
          )
            setOpen(false);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Escape" && open) {
            event.preventDefault();
            close();
          }
        }}
      >
        <button
          ref={trigger}
          className="account-trigger"
          type="button"
          aria-label="Account menu"
          title={`${user.name} · Account`}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => {
            setEditing(false);
            setError("");
            setOpen(!open);
          }}
        >
          <span className="account-avatar">
            {user.name.slice(0, 1).toUpperCase()}
          </span>
        </button>
        {open && (
          <div className="account-popover" id={panelId}>
            <div className="account-identity">
              <span className="account-avatar">
                {user.name.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <strong title={user.name}>{user.name}</strong>
                <span>Your account</span>
              </div>
            </div>
            {editing ? (
              <form
                className="account-profile-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (busy || !name.trim() || !saveName) return;
                  setBusy("save");
                  setError("");
                  try {
                    await saveName(name.trim());
                    setEditing(false);
                    close();
                  } catch (error) {
                    setError((error as Error).message);
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                <label>
                  Display name
                  <input
                    autoFocus
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={60}
                    required
                    disabled={busy === "save"}
                  />
                </label>
                <div className="account-form-actions">
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => {
                      setEditing(false);
                      setError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="account-save"
                    disabled={
                      !!busy || !name.trim() || name.trim() === user.name
                    }
                  >
                    {busy === "save" ? "Saving…" : "Save changes"}
                  </button>
                </div>
                {error && (
                  <p className="account-error" role="alert">
                    {error}
                  </p>
                )}
              </form>
            ) : (
              <div className="account-menu-items">
                {user.isOperator && (
                  <a className="account-menu-item" href="/?admin=overview">
                    <ShieldCheck size={16} />
                    <span>Admin</span>
                    <ChevronRight size={14} />
                  </a>
                )}
                {saveName && (
                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={() => {
                      setName(user.name);
                      setEditing(true);
                    }}
                  >
                    <Pencil size={16} />
                    <span>Edit profile</span>
                    <ChevronRight size={14} />
                  </button>
                )}
                {signOut && <button
                  type="button"
                  className="account-menu-item"
                  onClick={showUsage}
                >
                  <ChartNoAxesColumn size={16} />
                  <span>Account &amp; usage</span>
                  <ChevronRight size={14} />
                </button>}
                <button
                  type="button"
                  className="account-menu-item"
                  onClick={() => {
                    setPreferences(getPreferences(user.id));
                    close();
                    setPreferencesOpen(true);
                  }}
                >
                  <Settings2 size={16} />
                  <span>Preferences</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
            <div className="account-menu-footer">
              {signOut && <button
                type="button"
                className="account-menu-item account-sign-out"
                disabled={!!busy}
                onClick={async () => {
                  if (busy) return;
                  setBusy("sign-out");
                  try {
                    await signOut();
                  } catch (error) {
                    notify((error as Error).message);
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                <LogOut size={16} />
                <span>{busy === "sign-out" ? "Signing out…" : "Sign out"}</span>
              </button>}
              <span className="account-release">Calque · Early access</span>
            </div>
          </div>
        )}
      </div>
      {usage &&
        createPortal(
          <AccountUsage close={() => setUsage(false)} />,
          document.body,
        )}
      {preferencesOpen && createPortal(
        <Modal title="Preferences" close={() => setPreferencesOpen(false)}>
          <p>These defaults are saved on this device for your account. Project content stays shared.</p>
          <div className="form-grid">
            <label>Default measurement unit
              <select value={preferences.measurementUnit} onChange={(event) =>
                setPreferences(savePreferences(user.id, { measurementUnit: event.target.value as "mm" | "cm" | "m" }))}>
                <option value="mm">Millimetres</option>
                <option value="cm">Centimetres</option>
                <option value="m">Metres</option>
              </select>
            </label>
            <label>Opening view
              <select value={preferences.openingView} onChange={(event) =>
                setPreferences(savePreferences(user.id, { openingView: event.target.value as "fit" | "100" }))}>
                <option value="fit">Fit project</option>
                <option value="100">100% zoom</option>
              </select>
            </label>
            <label>Default drawing colour
              <select value={preferences.drawingColor} onChange={(event) =>
                setPreferences(savePreferences(user.id, { drawingColor: event.target.value }))}>
                {drawingColorChoices.map(([color, label]) => <option key={color} value={color}>{label}</option>)}
              </select>
            </label>
            {hasLocalAgents() && <label>Agents
              <select value={preferences.agentStyle} onChange={(event) =>
                setPreferences(savePreferences(user.id, { agentStyle: event.target.value as AgentStyle }))}>
                {agentStyleChoices.map(([style, label]) => <option key={style} value={style}>{label}</option>)}
              </select>
            </label>}
          </div>
          <small>Defaults apply when you next open a project. Existing marks keep their shared labels and styles.{hasLocalAgents() && " The agent style changes right away."}</small>
        </Modal>, document.body)}
    </>
  );
}
